import { ConfigProvider, Table } from 'antd';
import React, { FC, useCallback, useContext, useMemo } from 'react';
import { ROLE_TYPES } from '../../../utils/DataConstants';
import {
	BuyerWorkOrderTableEmptyState,
	SupplierWorkOrderTableEmptyState,
} from '../emptyState/WorkOrderTableEmptyState';
import WorkOrderTableRowBuyer from '../WorkOrderTableRow/buyer';
import WorkOrderTableRowSupplier from '../WorkOrderTableRow/supplier';
import { useWebSocketListener } from '../../../context/useWebSocketListener';
import { WEB_SOCKET_ENTITY_TYPES, WEB_SOCKET_MESSAGE_TYPES } from '../../../utils/WebSocketUtils';
import { WebSocketContext } from '../../../context/WebSocketContext';
import { withUserType } from '../../withUserType';
import { connect } from 'react-redux';
import {
	workOrdersRestCrudThunksForBuyer,
	workOrdersRestCrudThunksForSupplier,
} from '../../../thunks/work_orders_thunks';
import { getAllWOIdsToListenForWorkOrder } from '../../../utils/WorkOrderUtils';
import { nullSafeGet } from '../../../utils/DataAccessUtils';

const WOTable: FC<any> = (props) => {
	const {
		fetching,
		data,
		currentUser,
		companyConfig,
		CustomWorkOrderRowDisplay,
		CustomWorkOrderRowDisplayProps = {},
		refreshTable,
		tableStyle,
		renderEmptyState,
		userType,
		location,
		backText,
		targetCollectionName,
		rowSelection,
		refreshWOFromSearch,
		simplifyRows = false,
	} = props;

	const socketContext = useContext(WebSocketContext);

	const { entityIds, entityIdToWOIdMap } = useMemo(
		() =>
			(data || []).reduce(
				({ entityIds, entityIdToWOIdMap }, wo) => {
					const woId = nullSafeGet('id', wo);
					const relatedWOIds = getAllWOIdsToListenForWorkOrder(wo);
					return {
						entityIds: [...entityIds, ...relatedWOIds],
						entityIdToWOIdMap: relatedWOIds.reduce(
							(map, id) => {
								map[id] = woId;
								return map;
							},
							{ ...entityIdToWOIdMap }
						),
					};
				},
				{
					entityIds: [],
					entityIdToWOIdMap: {},
				}
			),
		[data]
	);

	const onMessage = useCallback(
		(event) => {
			try {
				const eventData = JSON.parse(event.data);

				if (!entityIds.some((id) => id == eventData.workOrderId)) return;

				switch (eventData.messageType) {
					// A new note or a label add/remove on a visible row: refetch that work
					// order from search so the row (and its label chips) update live for
					// users who already have this index open.
					case WEB_SOCKET_MESSAGE_TYPES.NEW_NOTE:
					case WEB_SOCKET_MESSAGE_TYPES.WO_LABEL_CHANGE: {
						const fetchWithWOId = entityIdToWOIdMap[eventData.workOrderId];
						if (!fetchWithWOId) return;

						refreshWOFromSearch(parseInt(fetchWithWOId));
						break;
					}
					default:
						break;
				}
			} catch (err) {
				// console.log('JSON parse error', err);
			}
		},
		[entityIdToWOIdMap, entityIds, refreshWOFromSearch]
	);

	useWebSocketListener({
		socketContext,
		onMessage,
		entityName: WEB_SOCKET_ENTITY_TYPES.WORK_ORDER,
		entityIds,
	});

	const EmptyState =
		userType === ROLE_TYPES.SUPPLIER
			? SupplierWorkOrderTableEmptyState
			: BuyerWorkOrderTableEmptyState;
	const WorkOrderTableRow =
		userType === ROLE_TYPES.SUPPLIER ? WorkOrderTableRowSupplier : WorkOrderTableRowBuyer;
	const columns = useMemo(
		() => [
			{
				render: (text, record) =>
					CustomWorkOrderRowDisplay ? (
						<CustomWorkOrderRowDisplay
							record={record}
							currentUser={currentUser}
							companyConfig={companyConfig}
							refreshTable={refreshTable}
							location={location}
							userType={userType}
							targetCollectionName={targetCollectionName}
							workOrder={record}
							backText={backText}
							{...CustomWorkOrderRowDisplayProps}
						/>
					) : (
						<WorkOrderTableRow
							record={record}
							currentUser={currentUser}
							companyConfig={companyConfig}
							refreshTable={refreshTable}
							location={location}
							backText={backText}
							targetCollectionName={targetCollectionName}
							simplify={simplifyRows}
						/>
					),
			},
		],
		[
			data,
			currentUser,
			companyConfig,
			refreshTable,
			location,
			userType,
			targetCollectionName,
			backText,
			simplifyRows,
		]
	);
	return (
		<ConfigProvider
			renderEmpty={() =>
				renderEmptyState
					? renderEmptyState(currentUser)
					: <EmptyState currentUser={currentUser} /> || undefined
			}
		>
			<Table
				rowClassName={'clickable-index-row'}
				style={{ tableLayout: 'fixed', ...tableStyle }}
				className="table-fixed-100"
				showHeader={false}
				pagination={false}
				loading={fetching}
				dataSource={data}
				columns={columns}
				rowSelection={rowSelection}
				rowKey={(record) => parseInt(record.id)}
			/>
		</ConfigProvider>
	);
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	refreshWOFromSearch: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? workOrdersRestCrudThunksForSupplier.read({ id })
				: workOrdersRestCrudThunksForBuyer.read({ id })
		),
});

export default withUserType(connect(null, mapDispatchToProps)(WOTable as any) as any);
